import * as Sentry from '@sentry/react';
import { PublicClientApplication } from '@azure/msal-browser';
import { getSubscriptionHeader } from './utils/api-manager';

export const IS_LOGIN_NOT_REQUIRED = process.env.REACT_APP_DISABLE_ACTIVE_DIRECTORY_LOGIN === 'true';
const AD_CLIENT_ID = process.env.REACT_APP_ACTIVE_DIRECTORY_CLIENT_ID || 'client-id';
const AD_TENANT = process.env.REACT_APP_ACTIVE_DIRECTORY_TENANT || 'tenant-name';
const AD_AUTHORITY = `https://login.microsoftonline.com/${AD_TENANT}`;

// Guarded so this module can be imported in non-browser environments (mocha/jest
// setup files), where `window` may not yet exist.
const APP_ORIGIN = typeof window !== 'undefined' ? window.location.origin : undefined;

const msalConfig = {
    auth: {
        clientId: AD_CLIENT_ID,
        authority: AD_AUTHORITY,
        // Pin to the registered origin so deep links (e.g. /control-main-view/...)
        // don't get sent as redirect_uri and rejected by AAD (AADSTS50011).
        redirectUri: APP_ORIGIN,
        postLogoutRedirectUri: APP_ORIGIN,
    },
    cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: true,
    },
};

const loginRequest = {
    scopes: ['User.Read'],
};

const guestUserName = 'Guest User';

const AUTH_BOOTSTRAP_COMPLETE_EVENT = 'at-command-centre:auth-bootstrap-complete';

const createAuthState = () => ({
    msalInstance: new PublicClientApplication(msalConfig),
    isBootstrapComplete: false,
});

// `src/index.jsx` and `src/indexApp.jsx` are separate webpack entry points, so
// module-level singletons in `auth.js` are duplicated at runtime. Keep the auth
// state on `window` so both bundles share the same MSAL instance and bootstrap
// latch.
const getSharedAuthState = () => {
    if (typeof window === 'undefined' || process.env.NODE_ENV === 'test') {
        return createAuthState();
    }

    const authStateKey = '__AT_COMMAND_CENTRE_AUTH_STATE__';
    window[authStateKey] = window[authStateKey] || createAuthState();
    return window[authStateKey];
};

const authState = getSharedAuthState();
const { msalInstance } = authState;
export const markAuthBootstrapComplete = () => {
    if (!authState.isBootstrapComplete) {
        authState.isBootstrapComplete = true;
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new window.Event(AUTH_BOOTSTRAP_COMPLETE_EVENT));
        }
    }
};

export const getMsalInstance = () => msalInstance;

// True while the URL still carries an OAuth response (?code=, #code=, etc.).
// During this window MSAL is mid-handleRedirectPromise and we must not start
// a new interactive request, otherwise we race with MSAL's internal
// navigation and end up capturing the wrong redirectStartPage.
const isReturningFromAuthRedirect = () => {
    if (typeof window === 'undefined') return false;
    const queryAndHash = window.location.search + window.location.hash;
    return /[?&#](code|error|state)=/.test(queryAndHash);
};

// Triggers loginRedirect while preserving the current deep link so the user
// is returned to the page they originally requested after authenticating.
// Idempotent in two ways:
//   1. No-op if MSAL is mid-redirect-handling (URL still carries the response).
//   2. Swallows `interaction_in_progress` so callers racing the bootstrap
//      don't crash — the in-flight interaction will complete on its own.
export const loginRedirectPreservingLocation = async () => {
    const startPage = typeof window !== 'undefined' ? window.location.href : undefined;
    if (isReturningFromAuthRedirect()) {
        return;
    }
    try {
        await msalInstance.loginRedirect({
            ...loginRequest,
            redirectStartPage: startPage,
        });
    } catch (err) {
        if (err && err.errorCode === 'interaction_in_progress') {
            return;
        }
        throw err;
    }
};

const getActiveOrCachedAccount = ({ promoteCachedAccount = true } = {}) => {
    const activeAccount = msalInstance.getActiveAccount();
    if (activeAccount) return activeAccount;

    const cachedAccount = msalInstance.getAllAccounts()[0];
    if (!cachedAccount || !promoteCachedAccount) return cachedAccount;

    msalInstance.setActiveAccount(cachedAccount);
    return cachedAccount;
};

const waitForAuthBootstrapComplete = async () => {
    if (authState.isBootstrapComplete) return;

    if (typeof window === 'undefined') return;

    await new Promise((resolve) => {
        const onBootstrapComplete = () => {
            window.removeEventListener(AUTH_BOOTSTRAP_COMPLETE_EVENT, onBootstrapComplete);
            resolve();
        };

        window.addEventListener(AUTH_BOOTSTRAP_COMPLETE_EVENT, onBootstrapComplete);

        if (authState.isBootstrapComplete) {
            window.removeEventListener(AUTH_BOOTSTRAP_COMPLETE_EVENT, onBootstrapComplete);
            resolve();
        }
    });
};

// The React bundle (indexApp.jsx) renders in parallel with MSAL bootstrap
// (index.jsx), so account reads can run before initialize /
// handleRedirectPromise / setActiveAccount complete. Promote a cached account
// when available; otherwise wait for bootstrap and try again.
const getAccountWhenReady = async () => {
    let account = getActiveOrCachedAccount();
    if (!account) {
        await waitForAuthBootstrapComplete();
        account = getActiveOrCachedAccount();
    }
    return account;
};

export const getAuthUser = async () => {
    if (IS_LOGIN_NOT_REQUIRED) { return { profile: { name: guestUserName } }; }
    // Auth bootstrap lives in src/index.jsx — do not trigger a new
    // loginRedirect from here. Components called during initial render must
    // tolerate a missing account; a redirect is already in flight if needed.
    const account = await getAccountWhenReady();
    if (!account) return undefined;
    Sentry.setUser({ username: account.username || guestUserName });
    return account;
};

export const getAuthToken = async () => {
    if (IS_LOGIN_NOT_REQUIRED) { return ''; }

    const account = await getAccountWhenReady();
    if (!account) {
        throw new Error('No user account found');
    }
    const forceRefresh = new Date() >= new Date(account.idTokenClaims.exp * 1000);
    const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh,
    });
    return response.idToken;
};

export const fetchWithAuthHeader = async (url, options) => {
    if (IS_LOGIN_NOT_REQUIRED) {
        return fetch(url, {
            ...options,
            headers: {
                ...(options && options.headers),
                ...getSubscriptionHeader(),
            },
        });
    }

    try {
        const idToken = await getAuthToken();
        const optionsWithToken = {
            ...options,
            headers: {
                ...options.headers,
                Authorization: `Bearer ${idToken}`,
                ...getSubscriptionHeader(),
            },
        };
        return fetch(url, optionsWithToken);
    } catch (error) {
        Sentry.captureException(error);
        return undefined;
    }
};

export const logout = () => {
    const account = msalInstance.getActiveAccount();
    msalInstance.setActiveAccount(null);
    msalInstance.logoutRedirect({ account });
    Sentry.setUser(null);
};
